import * as THREE from 'three';
import { TEXTURES } from '../config';
import type { BodyState, Universe } from '../astro/Universe';
import { TextureManager } from './TextureManager';
import {
  atmosphereFragment,
  atmosphereVertex,
  cloudFragment,
  glowFragment,
  glowVertex,
  planetFragment,
  planetVertex,
  ringFragment,
  ringVertex,
  skyFragment,
  skyVertex,
} from './shaders';

const sphereGeoHigh = new THREE.SphereGeometry(1, 384, 192);
const sphereGeoLow = new THREE.SphereGeometry(1, 96, 48);

type BodyView = {
  state: BodyState;
  group: THREE.Group;
  surface: THREE.Mesh<THREE.SphereGeometry, THREE.ShaderMaterial>;
  clouds?: THREE.Mesh<THREE.SphereGeometry, THREE.ShaderMaterial>;
  atmosphere?: THREE.Mesh<THREE.SphereGeometry, THREE.ShaderMaterial>;
  ring?: THREE.Mesh<THREE.RingGeometry, THREE.ShaderMaterial>;
  textureKeys: string[];
  cloudSpeed: number;
};

const tmpV = new THREE.Vector3();
const tmpM3 = new THREE.Matrix3();

// Galactic → equatorial (IAU): rows are galactic x (centre), y, z (north pole) in ICRF.
const galToEq = new THREE.Matrix3().set(
  -0.0548755604, 0.4941094279, -0.867666149,
  -0.8734370902, -0.4448296300, -0.1980763734,
  -0.4838350155, 0.7469822445, 0.4559837762,
);
const OBL = (23.43928 * Math.PI) / 180;
const eqToEcl = new THREE.Matrix3().set(1, 0, 0, 0, Math.cos(OBL), Math.sin(OBL), 0, -Math.sin(OBL), Math.cos(OBL));

export class SolarSystemView {
  readonly group = new THREE.Group();
  readonly textures: TextureManager;
  private readonly views: BodyView[] = [];
  private readonly sunGlow: THREE.Mesh<THREE.PlaneGeometry, THREE.ShaderMaterial>;
  private readonly sky: THREE.Mesh<THREE.SphereGeometry, THREE.ShaderMaterial>;
  readonly sunLight = new THREE.DirectionalLight(0xfff4e6, 3.2);
  readonly planetShine = new THREE.DirectionalLight(0x6f8fbf, 0);
  readonly ambient = new THREE.AmbientLight(0x8090b0, 0.05);
  /** camera-relative position of the sun (render space) */
  readonly sunRender = new THREE.Vector3();
  /** 0 in space / at night, 1 under a daylit sky: drives exposure and star fade */
  daylight = 0;

  constructor(renderer: THREE.WebGLRenderer, private readonly universe: Universe) {
    this.textures = new TextureManager(renderer);
    this.textures.onChange((key) => this.rebindTextures(key));

    // Sky
    this.sky = new THREE.Mesh(
      new THREE.SphereGeometry(1, 64, 32),
      new THREE.ShaderMaterial({
        uniforms: {
          map: { value: null },
          eclToGal: { value: new THREE.Matrix3() },
          brightness: { value: 0.55 },
        },
        vertexShader: skyVertex,
        fragmentShader: skyFragment,
        side: THREE.BackSide,
        depthWrite: false,
        depthTest: false,
      }),
    );
    // ecliptic → galactic = (galToEq)^T * (eqToEcl)^T ... galToEq rows are gal axes in eq frame
    const eclToEq = eqToEcl.clone().transpose();
    this.sky.material.uniforms.eclToGal.value.copy(galToEq).multiply(eclToEq);
    this.sky.renderOrder = -10;
    this.sky.frustumCulled = false;
    this.group.add(this.sky);

    // Sun glare billboard
    this.sunGlow = new THREE.Mesh(
      new THREE.PlaneGeometry(2, 2),
      new THREE.ShaderMaterial({
        uniforms: { color: { value: new THREE.Color(1.0, 0.85, 0.6) }, intensity: { value: 4 } },
        vertexShader: glowVertex,
        fragmentShader: glowFragment,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        depthTest: false,
        transparent: true,
      }),
    );
    this.sunGlow.frustumCulled = false;
    this.sunGlow.renderOrder = 5;
    this.group.add(this.sunGlow);

    this.group.add(this.sunLight, this.sunLight.target, this.planetShine, this.planetShine.target, this.ambient);

    for (const state of universe.bodies) this.views.push(this.createBodyView(state));
  }

  async preload(onProgress?: (done: number, total: number) => void): Promise<void> {
    const keys = new Map<string, { kind: 'color' | 'data'; ext: string }>();
    keys.set('stars', { kind: 'color', ext: 'jpg' });
    for (const v of this.views) {
      const d = v.state.def;
      keys.set(d.texture, { kind: 'color', ext: 'jpg' });
      if (d.nightTexture) keys.set(d.nightTexture, { kind: 'color', ext: 'jpg' });
      if (d.cloudTexture) keys.set(d.cloudTexture, { kind: 'color', ext: 'jpg' });
      if (d.normalTexture) keys.set(d.normalTexture, { kind: 'data', ext: 'jpg' });
      if (d.specTexture) keys.set(d.specTexture, { kind: 'data', ext: 'jpg' });
      if (d.ring) keys.set(d.ring.texture, { kind: 'color', ext: 'png' });
    }
    let done = 0;
    await Promise.all(
      [...keys].map(([k, o]) =>
        this.textures.preload(k, o.kind, o.ext).then(() => onProgress?.(++done, keys.size)),
      ),
    );
    for (const k of keys.keys()) this.rebindTextures(k);
    this.textures.requestHi('stars');
  }

  private createBodyView(state: BodyState): BodyView {
    const def = state.def;
    const group = new THREE.Group();
    const big = def.radius > 1e6;
    const surface = new THREE.Mesh(
      big ? sphereGeoHigh : sphereGeoLow,
      new THREE.ShaderMaterial({
        uniforms: {
          map: { value: null },
          nightMap: { value: null },
          normalMap: { value: null },
          specMap: { value: null },
          cloudMap: { value: null },
          hasNight: { value: def.nightTexture ? 1 : 0 },
          hasNormal: { value: def.normalTexture ? 1 : 0 },
          hasSpec: { value: def.specTexture ? 1 : 0 },
          hasCloudShadow: { value: def.id === 'earth' ? 1 : 0 },
          hasAtmosphere: { value: def.atmosphere ? 1 : 0 },
          tint: { value: new THREE.Vector3(...(def.tint ?? [1, 1, 1])) },
          sunPos: { value: this.sunRender },
          bodyRot: { value: new THREE.Matrix3() },
          cloudShift: { value: 0 },
          emissive: { value: def.kind === 'star' ? 6 : 0 },
          time: { value: 0 },
        },
        vertexShader: planetVertex,
        fragmentShader: planetFragment,
      }),
    );
    surface.frustumCulled = false;
    group.add(surface);

    const view: BodyView = { state, group, surface, textureKeys: [def.texture], cloudSpeed: 0 };
    if (def.nightTexture) view.textureKeys.push(def.nightTexture);
    if (def.normalTexture) view.textureKeys.push(def.normalTexture);
    if (def.specTexture) view.textureKeys.push(def.specTexture);

    if (def.cloudTexture) {
      view.textureKeys.push(def.cloudTexture);
      const venus = def.id === 'venus';
      view.cloudSpeed = venus ? 2 * Math.PI / (4 * 86400) : 2 * Math.PI / (6 * 86400);
      const clouds = new THREE.Mesh(
        sphereGeoHigh,
        new THREE.ShaderMaterial({
          uniforms: {
            cloudMap: { value: null },
            sunPos: { value: this.sunRender },
            cloudShift: { value: 0 },
            tint: { value: venus ? new THREE.Vector3(1.0, 0.92, 0.75) : new THREE.Vector3(1, 1, 1) },
            opacity: { value: venus ? 1.0 : 0.95 },
          },
          vertexShader: planetVertex,
          fragmentShader: cloudFragment,
          transparent: true,
          depthWrite: false,
          blending: THREE.CustomBlending,
          blendSrc: THREE.OneFactor,
          blendDst: THREE.OneMinusSrcAlphaFactor,
          side: THREE.DoubleSide,
        }),
      );
      clouds.frustumCulled = false;
      clouds.renderOrder = 1;
      clouds.userData.altitude = venus ? 60_000 : 9_000;
      view.clouds = clouds;
      group.add(clouds);
    }

    if (def.atmosphere) {
      const a = def.atmosphere;
      const atmo = new THREE.Mesh(
        sphereGeoLow,
        new THREE.ShaderMaterial({
          uniforms: {
            center: { value: new THREE.Vector3() },
            planetRadius: { value: def.radius },
            atmoRadius: { value: def.radius + a.height },
            sunDir: { value: new THREE.Vector3() },
            betaR: { value: new THREE.Vector3(...a.rayleigh) },
            betaM: { value: a.mie },
            scaleR: { value: a.rayleighScale },
            scaleM: { value: a.mieScale },
            sunIntensity: { value: a.sunIntensity },
          },
          vertexShader: atmosphereVertex,
          fragmentShader: atmosphereFragment,
          side: THREE.BackSide,
          transparent: true,
          depthWrite: false,
          depthTest: false,
          blending: THREE.CustomBlending,
          blendSrc: THREE.OneFactor,
          blendDst: THREE.SrcAlphaFactor,
        }),
      );
      atmo.frustumCulled = false;
      atmo.renderOrder = 2;
      view.atmosphere = atmo;
      group.add(atmo);
    }

    if (def.ring) {
      view.textureKeys.push(def.ring.texture);
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(def.ring.inner, def.ring.outer, 256, 1),
        new THREE.ShaderMaterial({
          uniforms: {
            ringMap: { value: null },
            inner: { value: def.ring.inner },
            outer: { value: def.ring.outer },
            center: { value: new THREE.Vector3() },
            planetRadius: { value: def.radius },
            sunPos: { value: this.sunRender },
          },
          vertexShader: ringVertex,
          fragmentShader: ringFragment,
          transparent: true,
          depthWrite: false,
          side: THREE.DoubleSide,
          blending: THREE.CustomBlending,
          blendSrc: THREE.OneFactor,
          blendDst: THREE.OneMinusSrcAlphaFactor,
        }),
      );
      ring.frustumCulled = false;
      ring.renderOrder = 1;
      view.ring = ring;
      this.group.add(ring);
    }

    this.group.add(group);
    return view;
  }

  private rebindTextures(key: string): void {
    if (key === 'stars') this.sky.material.uniforms.map.value = this.textures.get('stars');
    for (const v of this.views) {
      const d = v.state.def;
      const u = v.surface.material.uniforms;
      if (d.texture === key) u.map.value = this.textures.get(key);
      if (d.nightTexture === key) u.nightMap.value = this.textures.get(key);
      if (d.normalTexture === key) u.normalMap.value = this.textures.get(key);
      if (d.specTexture === key) u.specMap.value = this.textures.get(key);
      if (d.cloudTexture === key) {
        u.cloudMap.value = this.textures.get(key);
        if (v.clouds) v.clouds.material.uniforms.cloudMap.value = this.textures.get(key);
      }
      if (d.ring?.texture === key && v.ring) v.ring.material.uniforms.ringMap.value = this.textures.get(key);
    }
  }

  /** Place everything relative to the camera's world position (floating origin). */
  update(origin: THREE.Vector3, timeSeconds: number, camera: THREE.PerspectiveCamera, focus: THREE.Vector3): void {
    const sun = this.universe.get('sun');
    this.sunRender.subVectors(sun.position, origin);

    // Stream high-res textures for bodies the camera is near.
    const wanted = new Set<string>(['stars']);
    for (const v of this.views) {
      const dist = v.state.position.distanceTo(origin);
      if (dist < v.state.def.radius * TEXTURES.hiResWithinRadii) v.textureKeys.forEach((k) => wanted.add(k));
    }
    for (const v of this.views) {
      for (const k of v.textureKeys) {
        if (wanted.has(k)) this.textures.requestHi(k);
        else {
          const dist = v.state.position.distanceTo(origin);
          if (dist > v.state.def.radius * TEXTURES.unloadBeyondRadii) this.textures.releaseHi(k);
        }
      }
    }

    for (const v of this.views) {
      const s = v.state;
      const r = s.def.radius;
      tmpV.subVectors(s.position, origin);
      v.group.position.copy(tmpV);
      v.group.quaternion.copy(s.quaternion);
      v.surface.scale.setScalar(r - (v.surface.userData.sink ?? 0));
      const dist = tmpV.length();
      // Cull bodies smaller than a pixel; the HUD marks them instead.
      const pixelSize = (r / Math.max(dist, 1)) * (camera.getFilmHeight() > 0 ? 1000 : 1000);
      v.group.visible = pixelSize > 0.05;
      const u = v.surface.material.uniforms;
      tmpM3.setFromMatrix4(s.rotation);
      u.bodyRot.value.copy(tmpM3);
      u.time.value = timeSeconds;
      const shift = (timeSeconds * v.cloudSpeed) % (Math.PI * 2);
      u.cloudShift.value = shift;
      if (v.clouds) {
        v.clouds.scale.setScalar(r + v.clouds.userData.altitude);
        v.clouds.material.uniforms.cloudShift.value = shift;
      }
      if (v.atmosphere) {
        const au = v.atmosphere.material.uniforms;
        v.atmosphere.scale.setScalar(au.atmoRadius.value);
        au.center.value.copy(tmpV);
        au.sunDir.value.subVectors(this.sunRender, tmpV).normalize();
      }
      if (v.ring) {
        v.ring.position.copy(tmpV);
        v.ring.quaternion.setFromRotationMatrix(s.equatorFrame);
        v.ring.material.uniforms.center.value.copy(tmpV);
        v.ring.visible = v.group.visible;
      }
    }

    // Daylight: inside an atmosphere with the sun above the horizon.
    this.daylight = 0;
    for (const v of this.views) {
      const atm = v.state.def.atmosphere;
      if (!atm) continue;
      const rel = tmpV.subVectors(origin, v.state.position);
      const alt = rel.length() - v.state.def.radius;
      if (alt > atm.height) continue;
      const up = rel.normalize();
      const sunDir = new THREE.Vector3().subVectors(sun.position, origin).normalize();
      const elev = up.dot(sunDir);
      const inside = 1 - THREE.MathUtils.smoothstep(alt, atm.height * 0.05, atm.height * 0.6);
      this.daylight = Math.max(this.daylight, inside * THREE.MathUtils.smoothstep(elev, -0.12, 0.15));
    }
    this.sky.material.uniforms.brightness.value = 0.55 * (1 - 0.97 * this.daylight);

    // Sun glare: constant angular size so the sun reads from Neptune too.
    const sunDist = this.sunRender.length();
    const angular = Math.min(0.9, Math.max(0.06, (sun.def.radius / sunDist) * 2.2));
    this.sunGlow.position.copy(this.sunRender);
    this.sunGlow.scale.setScalar(sunDist * angular);
    this.sunGlow.visible = !this.isSunOccluded(origin);

    // Sunlight on ships and rocks
    this.sunLight.position.copy(focus).addScaledVector(this.sunRender.clone().sub(focus).normalize(), 1000);
    this.sunLight.target.position.copy(focus);
    this.sunLight.intensity = this.isSunOccluded(origin) ? 0 : 3.2;

    // Planet-shine from the dominant nearby body
    const near = this.universe.nearestSurface(origin);
    if (near.body.def.kind !== 'star' && near.altitude < near.body.def.radius * 4) {
      const toPlanet = near.body.position.clone().sub(origin).normalize();
      const sunDir = this.sunRender.clone().normalize();
      const litFraction = Math.max(0, -toPlanet.dot(sunDir) * 0.5 + 0.5);
      this.planetShine.color.set(near.body.def.mapColor);
      this.planetShine.intensity = 0.6 * litFraction * (1 - near.altitude / (near.body.def.radius * 4));
      this.planetShine.position.copy(focus).addScaledVector(toPlanet, 1000);
      this.planetShine.target.position.copy(focus);
    } else this.planetShine.intensity = 0;
  }

  /** True when a planet disc covers the sun as seen from the given point. */
  isSunOccluded(from: THREE.Vector3): boolean {
    const sun = this.universe.get('sun');
    const dir = tmpV.subVectors(sun.position, from);
    const sunDist = dir.length();
    dir.divideScalar(sunDist);
    for (const b of this.universe.bodies) {
      if (b.def.kind === 'star') continue;
      const oc = new THREE.Vector3().subVectors(b.position, from);
      const t = oc.dot(dir);
      if (t <= 0 || t > sunDist) continue;
      const d2 = oc.lengthSq() - t * t;
      if (d2 < b.def.radius * b.def.radius) return true;
    }
    return false;
  }

  bodyViewGroup(id: string): THREE.Group | undefined {
    return this.views.find((v) => v.state.def.id === id)?.group;
  }

  /** Sink the base globe slightly while terrain tiles draw on top of it. */
  setSurfaceSink(id: string, metres: number): void {
    const v = this.views.find((x) => x.state.def.id === id);
    if (v) v.surface.userData.sink = metres;
  }

  cloudShiftFor(id: string, timeSeconds: number): number {
    const v = this.views.find((x) => x.state.def.id === id);
    return v ? (timeSeconds * v.cloudSpeed) % (Math.PI * 2) : 0;
  }

  /** Hide the base globe below this altitude (terrain tiles take over). */
  setSurfaceVisible(id: string, visible: boolean): void {
    const v = this.views.find((x) => x.state.def.id === id);
    if (v) v.surface.visible = visible;
  }
}
